import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { WHATSAPP_DISPLAY, WHATSAPP_URL, WhatsApp } from '../home/ui';
import { Card, PageHead, orderStatus, orderLabel } from '../account/shared';

// "Chat con soporte" (diseño cuenta.html). El backend no tiene chat, así que
// esto es un asistente de respuestas fijas —dice que lo es— y todo lo demás
// se deriva a WhatsApp. No simula a una persona escribiendo.

const hora = () => new Date().toLocaleTimeString('es-GT', { hour: 'numeric', minute: '2-digit' });

const Chat = () => {
    const { userInfo } = useSelector(state => state.auth);
    const { myOrders } = useSelector(state => state.order);
    const nombre = (userInfo?.name || '').split(' ')[0];
    const enCurso = myOrders.find(o => orderStatus(o.delivery_status).group === 'proc');

    const RESPUESTAS = {
        '¿Dónde está mi pedido?': () => enCurso
            ? { text: `Tu pedido ${orderLabel(enCurso)} está: ${orderStatus(enCurso.delivery_status).label.toLowerCase()}. Puedes ver el seguimiento completo en el detalle.`, link: [`/dashboard/order/details/${enCurso._id}`, 'Ver seguimiento'] }
            : { text: 'No tienes pedidos en curso. El historial completo está en Mis pedidos.', link: ['/dashboard/my-orders', 'Ir a Mis pedidos'] },
        'Quiero cambiar un producto': () => ({ text: 'Tienes 30 días para cambios y devoluciones. Escríbenos por WhatsApp con tu número de pedido y coordinamos la recolección.', wa: true }),
        'Métodos de pago': () => ({ text: 'Por ahora los pedidos en línea se pagan contra entrega.' }),
        'Horario de atención': () => ({ text: `Te atendemos de 9:00 a 21:00, todos los días, por WhatsApp al ${WHATSAPP_DISPLAY}.`, wa: true }),
    };

    const [msgs, setMsgs] = useState(() => [{
        me: false, time: hora(),
        text: `¡Hola${nombre ? `, ${nombre}` : ''}! Soy el asistente automático de Señor Bigotes. Elige una pregunta frecuente o escríbenos por WhatsApp para hablar con una persona.`,
    }]);
    const [texto, setTexto] = useState('');
    const boxRef = useRef(null);

    useEffect(() => {
        if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
    }, [msgs]);

    const enviar = (t) => {
        t = (t || '').trim();
        if (!t) return;
        const r = RESPUESTAS[t]
            ? RESPUESTAS[t]()
            : { text: `Solo sé responder las preguntas frecuentes de abajo. Para esto escríbenos por WhatsApp al ${WHATSAPP_DISPLAY} y te responde una persona.`, wa: true };
        setMsgs(m => [...m, { me: true, text: t, time: hora() }, { me: false, time: hora(), ...r }]);
        setTexto('');
    };

    return (
        <section>
            <PageHead title='Chat con soporte' sub='Respuestas rápidas aquí; para hablar con una persona, WhatsApp de 9:00 a 21:00.' />
            <Card className='!p-0 overflow-hidden grid grid-rows-[auto_minmax(0,1fr)_auto] h-[600px] sm:h-[70vh]'>
                <div className='flex items-center gap-3 px-5 py-4 border-b border-[#f1efea] flex-wrap'>
                    <div className='w-[42px] h-[42px] rounded-full bg-[#0891b2] text-white grid place-items-center font-extrabold'>SB</div>
                    <div>
                        <b className='block text-[#0f172a] text-[15px]'>Asistente Bigotes</b>
                        <span className='text-[12.5px] text-[#64748b] font-semibold'>Respuestas automáticas</span>
                    </div>
                    <a href={WHATSAPP_URL} target='_blank' rel='noopener noreferrer'
                        className='ml-auto inline-flex items-center gap-2 h-10 px-4 rounded-[12px] bg-[#16a34a] hover:bg-[#15803d] text-white font-bold text-[13.5px]'>
                        <WhatsApp className='w-4 h-4' />Hablar con una persona
                    </a>
                </div>

                <div ref={boxRef} aria-live='polite' className='overflow-y-auto p-5 flex flex-col gap-[10px] bg-[#faf9f6]'>
                    {msgs.map((m, i) => (
                        <div key={i} className={`max-w-[72%] sm:max-w-[88%] px-[15px] py-[11px] rounded-[18px] text-[14.5px] leading-[1.45] ${m.me ? 'bg-[#0891b2] text-white rounded-br-[6px] self-end' : 'bg-white border border-[#f1efea] text-[#334155] rounded-bl-[6px] self-start'}`}>
                            {m.text}
                            {m.link && <Link to={m.link[0]} className='block mt-2 font-bold text-[#0891b2]'>{m.link[1]} →</Link>}
                            {m.wa && <a href={WHATSAPP_URL} target='_blank' rel='noopener noreferrer' className='block mt-2 font-bold text-[#16a34a]'>Abrir WhatsApp →</a>}
                            <small className='block text-[11px] mt-1 opacity-65'>{m.time}</small>
                        </div>
                    ))}
                </div>

                <div className='px-4 py-[14px] border-t border-[#f1efea] bg-white'>
                    <div className='flex gap-2 overflow-x-auto mb-[10px] [scrollbar-width:none]'>
                        {Object.keys(RESPUESTAS).map(q => (
                            <button key={q} type='button' onClick={() => enviar(q)}
                                className='px-[14px] py-[7px] rounded-full border-[1.5px] border-[#cffafe] text-[#0e7490] text-[13px] font-bold whitespace-nowrap hover:bg-[#ecfeff]'>
                                {q}
                            </button>
                        ))}
                    </div>
                    <form onSubmit={e => { e.preventDefault(); enviar(texto); }} className='flex gap-2'>
                        <input value={texto} onChange={e => setTexto(e.target.value)} placeholder='Escribe tu pregunta…' aria-label='Mensaje' autoComplete='off'
                            className='flex-1 min-w-0 h-12 border-[1.5px] border-[#e8e6e1] rounded-[14px] px-4 text-[15px] outline-none focus:border-[#0891b2]' />
                        <button aria-label='Enviar' className='w-12 h-12 rounded-[14px] bg-[#0891b2] hover:bg-[#0e7490] text-white grid place-items-center'>
                            <svg className='w-[18px] h-[18px]' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'><line x1='22' y1='2' x2='11' y2='13' /><polygon points='22 2 15 22 11 13 2 9 22 2' /></svg>
                        </button>
                    </form>
                </div>
            </Card>
        </section>
    );
};

export default Chat;
